import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Layers, Facebook, Instagram } from "lucide-react";
import PaymentBadges from "@/components/PaymentBadges";

/**
 * Shared footer — used on Landing and every other public page.
 * Includes social handles (Facebook: Resumelab.global, Instagram: Resumelabcv).
 */
export default function Footer() {
  const { t } = useTranslation();
  return (
    <footer className="border-t border-zinc-200/70 bg-white/60 backdrop-blur-sm" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 grid grid-cols-1 md:grid-cols-3 gap-8 items-start text-sm text-zinc-600">
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <Layers className="w-4 h-4 text-zinc-700" />
            <span className="font-display font-extrabold text-zinc-900">ResumeLab</span>
            <span className="text-zinc-500">· {t("footer.brand_tag")}</span>
          </div>
          <p className="text-xs text-zinc-500 leading-relaxed max-w-xs">
            {t("footer.brand_desc")}
          </p>
          <div className="flex items-center gap-3 mt-1" data-testid="footer-socials">
            <a
              href="https://www.facebook.com/Resumelab.global"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-facebook"
              aria-label="ResumeLab on Facebook (@Resumelab.global)"
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 border border-zinc-200 hover:border-zinc-400 hover:text-zinc-900 transition-colors text-xs"
            >
              <Facebook className="w-3.5 h-3.5" />
              <span className="font-medium">Resumelab.global</span>
            </a>
            <a
              href="https://www.instagram.com/Resumelabcv"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-instagram"
              aria-label="ResumeLab on Instagram (@Resumelabcv)"
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 border border-zinc-200 hover:border-zinc-400 hover:text-zinc-900 transition-colors text-xs"
            >
              <Instagram className="w-3.5 h-3.5" />
              <span className="font-medium">Resumelabcv</span>
            </a>
          </div>
        </div>

        <nav className="flex flex-col gap-2 text-xs">
          <span className="overline mb-1">{t("footer.product")}</span>
          <Link to="/evaluate" className="hover:text-zinc-900 transition-colors">{t("footer.evaluate_resume")}</Link>
          <Link to="/builder" className="hover:text-zinc-900 transition-colors">{t("footer.resume_builder")}</Link>
          <Link to="/evaluate/linkedin" className="hover:text-zinc-900 transition-colors">{t("footer.linkedin_optimizer")}</Link>
          <Link to="/interview/mock" className="hover:text-zinc-900 transition-colors">{t("footer.mock_interview")}</Link>
          <Link to="/interview/packs" className="hover:text-zinc-900 transition-colors">{t("footer.booster_packs")}</Link>
          <Link to="/templates" className="hover:text-zinc-900 transition-colors">{t("nav.templates")}</Link>
          <Link to="/pricing" className="hover:text-zinc-900 transition-colors">{t("nav.pricing")}</Link>
        </nav>

        <nav className="flex flex-col gap-2 text-xs">
          <span className="overline mb-1">{t("footer.company")}</span>
          <Link to="/blog" data-testid="footer-blog-link" className="hover:text-zinc-900 transition-colors">{t("nav.blog")}</Link>
          <Link to="/terms" data-testid="footer-terms-link" className="hover:text-zinc-900 transition-colors">{t("footer.terms")}</Link>
          <Link to="/privacy" data-testid="footer-privacy-link" className="hover:text-zinc-900 transition-colors">{t("footer.privacy")}</Link>
          <a href="mailto:hello@resumelab.co.ke" className="hover:text-zinc-900 transition-colors">hello@resumelab.co.ke</a>
        </nav>
      </div>
      <div className="border-t border-zinc-200/50">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 text-xs text-zinc-500 flex flex-col sm:flex-row justify-between items-center gap-3">
          <span>{t("footer.copyright", { year: new Date().getFullYear() })}</span>
          <PaymentBadges
            methods={["visa", "mastercard", "amex", "mpesa", "applepay", "googlepay"]}
            align="center"
            size="sm"
            label=""
          />
          <span>{t("footer.made_with")} · resumelab.co.ke</span>
        </div>
      </div>
    </footer>
  );
}
